import { getBaseUrl, Site, type ConversionData } from "@um/contracts";

// save the attribution data to local storage for when an account is created
const conversionDataKey = "_conversionData";

const urlParams = new URLSearchParams(window.location.search);
if (urlParams.has("utm_source")) {
  localStorage.setItem(
    conversionDataKey,
    JSON.stringify({
      // see https://ga-dev-tools.google/campaign-url-builder/
      utm_id: urlParams.get("utm_id") || undefined,
      utm_source: urlParams.get("utm_source") || undefined,
      utm_medium: urlParams.get("utm_medium") || undefined,
      utm_campaign: urlParams.get("utm_campaign") || undefined,
      utm_term: urlParams.get("utm_term") || undefined,
      utm_content: urlParams.get("utm_content") || undefined,
    })
  );
}

/**
 * The stashed attribution, for the account-create request.
 *
 * Read-only on purpose. This used to delete the stash as it read it — before
 * the create request had even been sent — so an attempt that failed or was
 * interrupted (the Facebook in-app browser closing mid-request, a cold-start
 * timeout) left the retry with nothing to send, and the account was created
 * with blank attribution. The stash is cleared by `clearAttributionData`
 * once a create has actually succeeded.
 */
export function getAttributionData() {
  return {
    conversionData: readStoredConversionData(),
  };
}

/**
 * Forget the stashed attribution. Called after an account exists for this
 * browser, so a later, unrelated signup on a shared device is not attributed
 * to a campaign it never came from.
 */
export function clearAttributionData(): void {
  localStorage.removeItem(conversionDataKey);
}

function readStoredConversionData(): Record<string, unknown> | undefined {
  const raw = localStorage.getItem(conversionDataKey);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch (error) {
    console.warn("Failed to parse conversion attribution data", error);
    return undefined;
  }
}

function applyConversionParams(
  linkedUrl: URL,
  conversionData: Record<string, unknown> | ConversionData
) {
  for (const [key, value] of Object.entries(conversionData)) {
    if (typeof value !== "string" || !value) continue;
    linkedUrl.searchParams.set(key, value);
  }
}

/**
 * Build a site URL with attribution query params.
 * Prefers localStorage `_conversionData` when present; falls back to the
 * optional `conversionData` arg (e.g. from User after signup cleared storage).
 */
export function getUrlWithAttribution(
  site: Site,
  path?: string,
  conversionData?: ConversionData | Record<string, unknown> | null
) {
  const isEmulator = import.meta.env.VITE_USE_EMULATORS === "true"; // break cirular import dependency by duplicating logic
  const useDeepLink = path && site == "appNative" && !isEmulator;
  let linkedUrl = new URL(getBaseUrl(site));
  if (useDeepLink) {
    // pass the path as a deep link when using the native app
    linkedUrl.searchParams.set("deep_link_value", path);
  } else if (path) {
    linkedUrl = new URL(path, linkedUrl);
  }

  const data = readStoredConversionData() ?? conversionData;
  if (data) {
    applyConversionParams(linkedUrl, data);
  }

  // propogate the test group
  const testGroupId = localStorage.getItem("selectedTestGroupId"); // break cirular import dependency by duplicating logic
  if (testGroupId) {
    linkedUrl.searchParams.set("test", testGroupId);
  }

  return linkedUrl.toString();
}
